/*
 * popup-engine.js  (Popups slice — Unit A1 foundation + Unit A2 show-logic)
 *
 * Shared, deferred, single-copy engine for all popup sections.
 *
 * A1 (foundation): discover [data-popup-id] live <dialog>s -> arm the `timed` trigger
 *   -> on fire, fetch the popup's CSS -> showModal() the existing live <dialog>
 *   (top-layer + free focus-trap/Escape/inert-bg) -> close (close button / Escape /
 *   backdrop click) -> iOS-safe scroll restore. The dialog is persistent (re-openable),
 *   never removed from the DOM.
 *
 * A2 (show-logic, additive — no A1 renames): the remaining triggers (scroll-depth,
 *   time-on-page, exit-intent, cart-value) + the click/ID + anchor trigger;
 *   two-state frequency (dismissed vs converted) + the campaign version stamp +
 *   pop_reset_token; new-vs-returning visitor; the one-popup-per-pageview
 *   coordinator + multi-instance arbitration; test mode + editor select/deselect
 *   preview; close->setDismissed; the popup:converted listener (for A3/B).
 *
 * A3 (capture + launcher, additive — no A1/A2 renames): the native {% form 'customer' %}
 *   submit handler (real validation via reportValidity, colon-namespaced dynamic auto-tags)
 *   using native requestSubmit() + full reload + a generic reopen-on-result mechanism
 *   (stash before submit -> ?customer_posted=true detection -> open + popup:reopened +
 *   setConverted), NOT AJAX; the floating launcher (FAB) click-bind + converted-gating +
 *   the loadAssets dedup-by-existing-DOM-link tweak.
 *
 * Deferred to later units (NOT here): the spin wheel + segment data + the spin-specific
 * submit re-wire (instant-pick -> submit-with-prize-tag) + warm-prefetch (B); seal (C).
 *
 * Rules: J12 (IIFE-module dedup guard), J1/J2 (addEventListener only, no inline
 * handlers), C2 (logical CSS on the JS-built sentinel), no window.theme pollution.
 */
(function () {
  'use strict';
  if (window.__POPUP_ENGINE__) return;
  window.__POPUP_ENGINE__ = true;

  var loadedAssets = new Set(); // CSS URLs already injected (dedup)
  var armed = new Set();        // popup ids already processed by discover (re-scan safe)
  var openIds = new Set();      // currently-open ids (double-open guard)
  var configs = new Map();      // id -> config object
  var savedScrollY = 0;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ANIM_FALLBACK_MS = 400;   // safety net if transitionend never fires

  // --- A2 module state ---
  var triggerMap = {};                 // triggerId (#id) -> popup id, rebuilt each discover()
  var convertedOpen = new Set();       // ids converted during the current open (suppresses dismissed write)
  var clickBound = false;              // delegated click listener bound once
  var warmBound = false;               // B: delegated hover/focus warm-prefetch listener bound once
  var visitorSeenSnapshot = null;      // site visitor flag captured-once-per-pageview (null = uncaptured)
  var AUTO_TRIGGERS = ['timed', 'scroll_depth', 'exit_intent', 'cart_value'];
  var DAY_MS = 86400000, WEEK_MS = 604800000;

  // --- Eager-render + generic reopen module state ---
  var dialogById = new Map();   // id -> the LIVE <dialog> node (populated at discover; persistent)
  var pendingReopen = new Map();// id -> payload, consumed inside open() after popup:opened (reopen ordering)
  var submitting = new Set();   // ids whose native requestSubmit() has fired (double-submit guard)
  var reopenChecked = false;    // maybeReopen() runs exactly once per page load
  var REOPEN_KEY = 'popup:reopen';
  var STALE_MS = 300000;        // 5 min stale-guard for an orphaned stash

  function readConfig(wrapper) {
    var d = wrapper.dataset;
    return {
      id: d.popupId,
      name: d.popupName || '',
      trigger: d.popupTrigger || 'timed',
      delay: parseInt(d.popupDelay, 10) || 0,
      engineCss: d.popupEngineCss || '',
      contentCss: d.popupCss || '',
      contentJs: d.popupJs || '',           // B: data-popup-js (wheel behaviour JS; blank for simple popups)
      position: d.popupPosition || 'center',
      animation: d.popupAnimation || 'fade',
      closeOnBackdrop: d.popupCloseOnBackdrop === 'true',
      showClose: d.popupShowClose === 'true',
      // --- A2 additive keys (no A1 renames) ---
      scrollPct: parseInt(d.popupScrollPct, 10) || 0,     // data-popup-scroll-pct
      cartThreshold: parseFloat(d.popupCartThreshold) || 0, // data-popup-cart-threshold (major units)
      frequency: d.popupFrequency || 'session',           // data-popup-frequency
      priority: parseInt(d.popupPriority, 10) || 0,       // data-popup-priority
      visitor: d.popupVisitor || 'all',                   // data-popup-visitor
      triggerId: d.popupTriggerId || '',                  // data-popup-trigger-id
      campaign: d.popupCampaign || '',                    // data-popup-campaign
      resetToken: d.popupResetToken || '',                // data-popup-reset-token
      testMode: d.popupTestMode === 'true',               // data-popup-test-mode
      // --- A3 additive keys (dynamic auto-tag flags; no A1/A2 renames) ---
      autotagDevice: d.popupAutotagDevice === 'true',     // data-popup-autotag-device
      autotagTrigger: d.popupAutotagTrigger === 'true',   // data-popup-autotag-trigger
      autotagSource: d.popupAutotagSource === 'true',     // data-popup-autotag-source
      autotagDate: d.popupAutotagDate === 'true',         // data-popup-autotag-date
      autotagPrize: d.popupAutotagPrize === 'true'        // data-popup-autotag-prize (B reads this)
    };
  }

  // ---------- A2: campaign version stamp (R-POPUP-11) ----------
  // Seed assembly is isolated so Unit B can append the wheel's segment data
  // (read from the live hidden dialog) without touching the hashing.
  function stampSeed(cfg) {
    // A2 seed = section campaign copy + manual reset token.
    var seed = (cfg.campaign || '') + '|' + (cfg.resetToken || '');
    // B: fold in each wheel segment's CAMPAIGN-DEFINING data (label|code|weight|loss),
    // read from the LIVE (hidden) dialog (S20-safe — self-emitted DOM attributes, never
    // section.blocks). Cosmetic-only fields (colors, emojis, result title/message) are
    // EXCLUDED so visual tweaks don't reset the audience (R-POPUP-11).
    var dlg = dialogById.get(cfg.id);
    if (dlg) {
      var segs = dlg.querySelectorAll('.psw-seg');
      for (var i = 0; i < segs.length; i++) {
        var s = segs[i].dataset;
        seed += '|' + (s.label || '') + '|' + (s.code || '') + '|' + (s.weight || '') + '|' + (s.loss || '');
      }
    }
    return seed;
  }
  function djb2(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) { h = ((h << 5) + h) + str.charCodeAt(i); h = h | 0; }
    return (h >>> 0).toString(36); // compact, positive base36
  }
  function stamp(cfg) { return djb2(stampSeed(cfg)); }

  // ---------- A2: storage helpers (private-mode safe) ----------
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }

  function dismissKey(cfg) { return 'pop_dismissed_' + cfg.id + '_' + stamp(cfg); }
  function doneKey(cfg) { return 'pop_done_' + cfg.id + '_' + stamp(cfg); }
  function sesKey(cfg) { return 'pop_ses_' + cfg.id + '_' + stamp(cfg); } // stamped so reshow-on-change works within a session too

  function getDismissed(cfg) { var v = lsGet(dismissKey(cfg)); return v ? parseInt(v, 10) : null; }
  function setDismissed(cfg) {
    lsSet(dismissKey(cfg), String(Date.now()));
    if (cfg.frequency === 'session') ssSet(sesKey(cfg), '1');
  }
  function getConverted(cfg) { return !!lsGet(doneKey(cfg)); }
  function setConverted(cfg) { lsSet(doneKey(cfg), String(Date.now())); }

  // Site-level visitor flag: read-before-set, snapshot once per pageview so every
  // popup evaluated this load sees the same "was this a returning visitor?" answer.
  function visitorWasSeen() {
    if (visitorSeenSnapshot === null) {
      visitorSeenSnapshot = !!lsGet('pop_visitor_seen');
      lsSet('pop_visitor_seen', '1'); // set once per browser after first eligibility eval
    }
    return visitorSeenSnapshot;
  }

  // ---------- A2: eligibility gate (AUTO triggers only) ----------
  function eligible(cfg) {
    if (cfg.testMode || (window.Shopify && Shopify.designMode)) return true;
    if (getConverted(cfg)) return false; // converted = persistent suppress until stamp changes
    var dism = getDismissed(cfg), now = Date.now(), f = cfg.frequency;
    if (f === 'session') { if (ssGet(sesKey(cfg))) return false; }
    else if (f === 'daily') { if (dism && (now - dism) < DAY_MS) return false; }
    else if (f === 'weekly') { if (dism && (now - dism) < WEEK_MS) return false; }
    else if (f === 'once') { if (dism) return false; }
    // 'always' -> no dismiss gate
    var seen = visitorWasSeen();
    if (cfg.visitor === 'new' && seen) return false;
    if (cfg.visitor === 'returning' && !seen) return false;
    return true;
  }

  // A3: a URL may already be in the DOM as an eager <link> (the launcher eager-loads
  // popup-engine.css so the FAB is styled at load). Check the DOM, not just the JS Set,
  // so the on-fire fetch never double-injects.
  function linkExists(url) {
    if (!url) return false;
    var ls = document.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < ls.length; i++) {
      if (ls[i].href === url || ls[i].getAttribute('href') === url) return true;
    }
    return false;
  }

  // Inject each CSS URL once; resolve when it (or all already-loaded) finishes.
  function loadAssets(urls) {
    return Promise.all(urls.map(function (url) {
      if (!url || loadedAssets.has(url) || linkExists(url)) { loadedAssets.add(url); return Promise.resolve(); }
      loadedAssets.add(url);
      return new Promise(function (resolve) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.addEventListener('load', resolve);
        link.addEventListener('error', resolve); // never hang the open on a 404
        document.head.appendChild(link);
      });
    }));
  }

  // B: a URL may already be present as a <script> (e.g. warm-prefetched). DOM-dedup
  // like linkExists so the on-fire inject never double-runs the wheel IIFE.
  function scriptExists(url) {
    if (!url) return false;
    var ss = document.querySelectorAll('script[src]');
    for (var i = 0; i < ss.length; i++) {
      if (ss[i].src === url || ss[i].getAttribute('src') === url) return true;
    }
    return false;
  }

  // B: inject the popup's behaviour JS once; resolve when it has loaded (and thus
  // executed + registered its popup:opened listener). Blank url resolves immediately.
  function loadScript(url) {
    if (!url || loadedAssets.has(url) || scriptExists(url)) { loadedAssets.add(url); return Promise.resolve(); }
    loadedAssets.add(url);
    return new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = url;
      s.defer = true;
      s.addEventListener('load', resolve);
      s.addEventListener('error', resolve); // never hang the open on a 404
      document.head.appendChild(s);
    });
  }

  // B: warm-prefetch a popup's heavy assets without showing it (deduped, non-blocking).
  // Never in designMode. Purely latency — does not change open/eligibility.
  function warmPrefetch(cfg) {
    if (!cfg || (window.Shopify && Shopify.designMode)) return;
    try {
      loadAssets([cfg.engineCss, cfg.contentCss].filter(Boolean));
      loadScript(cfg.contentJs);
    } catch (e) {}
  }

  // iOS-safe scroll lock (NOT body.overflow=hidden — that fails iOS rubber-band).
  function lockScroll() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    var s = document.body.style;
    s.position = 'fixed';
    s.insetBlockStart = '-' + savedScrollY + 'px';
    s.inlineSize = '100%';
  }

  function unlockScroll() {
    var s = document.body.style;
    s.position = '';
    s.insetBlockStart = '';
    s.inlineSize = '';
    window.scrollTo(0, savedScrollY);
  }

  // B: a spin-wheel needs >= 2 segments. Read the count from the LIVE (hidden) dialog
  // (never section.blocks — S20). Detect "is a wheel" via the .psw root so simple
  // popups (no .psw) are never blocked; a wheel with 0 or 1 .psw-seg is blocked.
  function wheelSegBlocked(cfg) {
    var dlg = dialogById.get(cfg.id);
    if (!dlg || !dlg.querySelector('.psw')) return false; // not a wheel -> never blocked
    return dlg.querySelectorAll('.psw-seg').length < 2;    // a wheel needs >= 2 segments
  }

  function open(id) {
    var cfg = configs.get(id);
    if (!cfg || openIds.has(id)) return;
    if (wheelSegBlocked(cfg)) {
      if (window.Shopify && Shopify.designMode) {
        console.warn('[popup] spin-wheel "' + id + '" needs at least 2 segments — not opened.');
      }
      return;
    }
    var dialog = dialogById.get(id) || document.querySelector('dialog.popup[data-popup-id="' + id + '"]');
    if (!dialog) return;
    openIds.add(id);
    convertedOpen.delete(id);

    Promise.all([
      loadAssets([cfg.engineCss, cfg.contentCss].filter(Boolean)),
      loadScript(cfg.contentJs)   // blank for simple popups -> resolves instantly
    ]).then(function () {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', ''); // ancient-browser fallback
      }

      if (reduce.matches) {
        dialog.classList.add('is-open');
      } else {
        requestAnimationFrame(function () { dialog.classList.add('is-open'); });
      }

      lockScroll();
      document.dispatchEvent(new CustomEvent('popup:opened', { detail: { id: id } }));

      // Generic reopen-on-reload: consume a pending payload AFTER popup:opened so a
      // consumer's init (e.g. the wheel drawing its canvas) has registered first.
      if (pendingReopen.has(id)) {
        var payload = pendingReopen.get(id);
        pendingReopen.delete(id);
        document.dispatchEvent(new CustomEvent('popup:reopened', { detail: { id: id, payload: payload } }));
        convertedOpen.add(id);
        setConverted(cfg);          // persistent converted lock (no future auto-open)
        hideLauncherFor(id);        // a converted popup hides its launcher
      }
    });
  }

  function close(dialog) {
    var id = dialog.dataset.popupId;
    if (!openIds.has(id)) return;

    function finalize() {
      if (dialog.open) dialog.close();
      // Persistent dialog — re-openable; never removed from the DOM.
      unlockScroll();
      openIds.delete(id);

      // A2: write the dismissed-state storage UNLESS converted this open, test, or design mode.
      var cfg = configs.get(id);
      if (cfg && !convertedOpen.has(id) && !cfg.testMode && !(window.Shopify && Shopify.designMode)) {
        setDismissed(cfg);
      }
      convertedOpen.delete(id);

      // A1 `timed` popups have no trigger element, so document focus simply returns
      // to the body after the modal is removed. (Trigger return-focus arrives in A3.)
      document.dispatchEvent(new CustomEvent('popup:closed', { detail: { id: id } }));
    }

    dialog.classList.remove('is-open');

    if (reduce.matches) {
      finalize();
      return;
    }

    var card = dialog.querySelector('.popup__card');
    var done = false;
    function onEnd() {
      if (done) return;
      done = true;
      if (card) card.removeEventListener('transitionend', onEnd);
      finalize();
    }
    if (card) card.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, ANIM_FALLBACK_MS); // safety net (e.g. fade with no transform delta)
  }

  // Eager render: the dialog + form are in the DOM at load, so close + submit bind
  // ONCE per live dialog at discover (idempotent via a dataset flag), not per-open.
  function bindDialog(dialog, cfg) {
    if (dialog.dataset.popupBound === 'true') return;
    dialog.dataset.popupBound = 'true';
    bindClose(dialog, cfg);
    var form = dialog.querySelector('form');
    if (form) form.addEventListener('submit', function (e) { handleCaptureSubmit(e, cfg.id, cfg); });
  }

  function bindClose(dialog, cfg) {
    var closeBtn = dialog.querySelector('[data-popup-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { close(dialog); });

    // Escape / native cancel — preventDefault so we run the animated + scroll-restore path.
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      close(dialog);
    });

    if (cfg.closeOnBackdrop) {
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) close(dialog); // landed on backdrop area outside .popup__card
      });
    }
  }

  // ---------- A2: trigger mechanisms ----------
  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // scroll-depth: a 1px sentinel at scrollPct% of doc height, observed once (IO).
  function armScroll(cfg) {
    var h = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    var s = document.createElement('div');
    s.style.cssText = 'position:absolute;inset-inline-start:0;inline-size:1px;block-size:1px;pointer-events:none;';
    s.style.insetBlockStart = Math.round(h * (cfg.scrollPct / 100)) + 'px';
    document.body.appendChild(s);
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        io.disconnect();
        if (s.parentNode) s.parentNode.removeChild(s);
        open(cfg.id);
      }
    });
    io.observe(s); // placed once on arm (no recompute)
  }

  // exit-intent: desktop only; fire when the cursor leaves the top of the viewport.
  function armExit(cfg) {
    if (window.matchMedia('(pointer:coarse)').matches || window.innerWidth < 750) return;
    function onOut(e) {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        document.removeEventListener('mouseout', onOut);
        open(cfg.id);
      }
    }
    document.addEventListener('mouseout', onOut);
  }

  // cart-value: /cart.js once on arm + cart:updated listener; total_price is in
  // minor units, threshold is in major units -> compare against threshold*100.
  function armCart(cfg) {
    var fired = false, threshold = cfg.cartThreshold * 100;
    function check(total) {
      if (!fired && typeof total === 'number' && total >= threshold) {
        fired = true;
        document.removeEventListener('cart:updated', onUpd);
        open(cfg.id);
      }
    }
    function onUpd(e) {
      var c = e.detail && e.detail.cart;
      if (c && typeof c.total_price === 'number') check(c.total_price);
      else fetchCart().then(function (cart) { if (cart) check(cart.total_price); });
    }
    document.addEventListener('cart:updated', onUpd);
    fetchCart().then(function (cart) { if (cart) check(cart.total_price); });
  }

  function armTrigger(cfg) {
    switch (cfg.trigger) {
      case 'timed':
        setTimeout(function () { open(cfg.id); }, cfg.delay * 1000); // delay is now seconds
        // B: warm the heavy assets ~2s before the scheduled show so the open is instant.
        var warmAt = Math.max(0, cfg.delay - 2) * 1000;
        var schedule = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1); };
        schedule(function () { setTimeout(function () { warmPrefetch(cfg); }, warmAt); });
        break;
      case 'scroll_depth': armScroll(cfg); break;
      case 'exit_intent': armExit(cfg); break;
      case 'cart_value': armCart(cfg); break;
      // 'click' is NOT armed here — handled by the delegated click listener.
    }
  }

  // ---------- A2: click / ID + anchor trigger (bound once, for ALL popups) ----------
  function bindClickTriggers() {
    if (clickBound) return; // delegated listener bound once; triggerMap is rebuilt live
    clickBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var openEl = t.closest('[data-popup-open]');
      if (openEl) {
        var id = triggerMap[openEl.getAttribute('data-popup-open')];
        if (id) { if (openEl.tagName === 'A') e.preventDefault(); open(id); return; }
      }
      var a = t.closest('a[href]');
      if (a) {
        var href = a.getAttribute('href') || '', i = href.indexOf('#');
        if (i !== -1) {
          var frag = href.slice(i + 1);
          if (triggerMap[frag]) { e.preventDefault(); open(triggerMap[frag]); return; } // a[href$="#<id>"], incl. menu links
        }
      }
    });
  }

  // ---------- B: warm-prefetch on hover/focus of click/ID/anchor triggers ----------
  function bindWarmHovers() {
    if (warmBound) return; // delegated, bound once; triggerMap is live
    warmBound = true;
    function warmFromEvent(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var id = null;
      var openEl = t.closest('[data-popup-open]');
      if (openEl) id = triggerMap[openEl.getAttribute('data-popup-open')];
      if (!id) {
        var a = t.closest('a[href]');
        if (a) {
          var href = a.getAttribute('href') || '', i = href.indexOf('#');
          if (i !== -1) id = triggerMap[href.slice(i + 1)];
        }
      }
      if (id) warmPrefetch(configs.get(id));
    }
    document.addEventListener('pointerover', warmFromEvent);
    document.addEventListener('focusin', warmFromEvent);
  }

  // ---------- A3: capture-form tag assembly (colon-namespaced) ----------
  // Sanitize a dynamic value before tagging: drop commas (the tag separator) + trim.
  function clean(s) { return String(s == null ? '' : s).replace(/,/g, '').trim(); }

  function isoDate() {
    var d = new Date();
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
  }

  function sourceTag() {
    try {
      var u = new URLSearchParams(window.location.search).get('utm_source');
      if (u) return clean(u);
      if (document.referrer) {
        var h = new URL(document.referrer).hostname;
        if (h && h !== window.location.hostname) return clean(h);
      }
    } catch (e) {}
    return '';
  }

  // Append the enabled dynamic auto-tags to the hidden contact[tags] value just before
  // submit. Recomputed from data-popup-tags-base each time so retries are idempotent.
  function appendDynamicTags(form, cfg) {
    var input = form.querySelector('input[name="contact[tags]"]');
    if (!input) return;
    var base = input.getAttribute('data-popup-tags-base');
    if (base === null) base = input.value || '';
    var parts = base.split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    if (cfg.autotagDevice) {
      var mobile = window.matchMedia('(pointer:coarse)').matches || window.innerWidth < 750;
      parts.push('popup:device:' + (mobile ? 'mobile' : 'desktop'));
    }
    if (cfg.autotagTrigger) parts.push('popup:trigger:' + clean(cfg.trigger));
    if (cfg.autotagSource) { var s = sourceTag(); if (s) parts.push('popup:source:' + s); }
    if (cfg.autotagDate) parts.push('popup:date:' + isoDate());
    // B: the wheel sets form.dataset.popupPrize on popup:willsubmit (before this runs).
    if (cfg.autotagPrize && form.dataset.popupPrize) parts.push('popup:prize:' + clean(form.dataset.popupPrize));

    input.value = parts.filter(Boolean).join(',');
  }

  // ---------- Native-submit capture + generic reopen-on-reload ----------
  // Native-submit capture: hold the submit, let a consumer (the wheel) run its spin,
  // then fire a real form.requestSubmit() so hCaptcha gates it and the page reloads.
  function handleCaptureSubmit(e, id, cfg) {
    var form = e.target;
    // Programmatic pass-through: finalizeSubmit set this flag, so let the NATIVE POST run.
    if (form.dataset.popupGo === '1') return;

    e.preventDefault(); // hold the native POST while we validate + (optionally) spin
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    var submit = form.querySelector('.popup__submit');
    if (submit) submit.setAttribute('disabled', ''); // double-submit guard (wheel also relabels)

    // Cancelable: a deferring consumer (the wheel) calls preventDefault() to claim
    // "hold — I'll signal completion via popup:spincomplete". A non-deferring consumer
    // (a simple popup) leaves it un-prevented -> we submit immediately below.
    var ev = new CustomEvent('popup:willsubmit', { detail: { id: id, form: form }, cancelable: true });
    document.dispatchEvent(ev);

    if (ev.defaultPrevented) return;     // wheel deferred -> wait for popup:spincomplete
    finalizeSubmit(id, cfg, form);       // simple popup -> submit now
  }

  // Append dynamic tags, ensure a reopen stash exists, then fire the NATIVE submit.
  function finalizeSubmit(id, cfg, form) {
    if (submitting.has(id) || form.dataset.popupGo === '1') return;
    submitting.add(id);
    if (cfg) appendDynamicTags(form, cfg);     // incl. popup:prize: from form.dataset.popupPrize
    ensureStash(id);                           // default success stash if the consumer wrote none
    form.dataset.popupGo = '1';                // next submit event passes through natively
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  // Generic reopen mechanism — stash + reload detection ------------------------------
  function readStash() {
    var raw = ssGet(REOPEN_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  function clearStash() { try { window.sessionStorage.removeItem(REOPEN_KEY); } catch (e) {} }

  // Write a default success stash ONLY if the consumer didn't already write its own.
  function ensureStash(id) {
    var s = readStash();
    if (s && s.id === id) return; // consumer (the wheel) already stashed its payload
    ssSet(REOPEN_KEY, JSON.stringify({ id: id, payload: { kind: 'success' }, t: Date.now() }));
  }

  // Run once on init (end of discover): if we landed on ?customer_posted=true or
  // ?contact_posted=true (both detected below) AND a stash exists, reopen that popup
  // directly on its result (bypasses the coordinator). The stash is keyed by popup id,
  // so contact popups (Slice 22) reuse this path unchanged.
  function maybeReopen() {
    var data = readStash();
    var posted = /[?&](customer|contact)_posted=true/.test(window.location.search || '');
    if (!posted) {
      if (data && (!data.t || (Date.now() - data.t) > STALE_MS)) clearStash(); // orphan stale-guard
      return;
    }
    if (!data || !data.id) return;
    var cfg = configs.get(data.id);
    if (!cfg) return; // popup not on this page (targeting) — leave stash for the page that has it
    pendingReopen.set(data.id, data.payload);
    clearStash();      // payload now rides in pendingReopen + the popup:reopened event
    open(data.id);     // bypasses eligibility/coordinator; open() consumes pendingReopen
  }

  // ---------- A3: floating launcher (FAB) binding + converted gating ----------
  function bindLaunchers() {
    var fabs = document.querySelectorAll('[data-popup-launcher]');
    for (var i = 0; i < fabs.length; i++) {
      var fab = fabs[i], pid = fab.dataset.popupId;
      if (fab.dataset.popupLauncherBound !== 'true') {
        fab.dataset.popupLauncherBound = 'true';
        (function (id) {
          fab.addEventListener('click', function () { open(id); });
          // B: warm the heavy assets on hover/focus so the click-to-open feels instant.
          var warm = function () { warmPrefetch(configs.get(id)); };
          fab.addEventListener('pointerover', warm);
          fab.addEventListener('focusin', warm);
        })(pid);
      }
      // Hide once converted (matches the example's hide-once-converted). Dismissed users keep
      // the FAB (explicit reopen always allowed). designMode never hides (merchant must see it).
      var cfg = configs.get(pid);
      if (cfg && getConverted(cfg) && !(window.Shopify && Shopify.designMode)) fab.classList.add('is-converted');
    }
  }

  function hideLauncherFor(id) {
    var fabs = document.querySelectorAll('[data-popup-launcher][data-popup-id="' + id + '"]');
    for (var i = 0; i < fabs.length; i++) fabs[i].classList.add('is-converted');
  }

  // ---------- A2: one-popup-per-pageview coordinator + multi-instance arbitration ----------
  function discover() {
    var wrappers = document.querySelectorAll('[data-popup-id]:not([data-popup-launcher])');
    var pending = []; // not-yet-processed AUTO candidates this pass
    for (var i = 0; i < wrappers.length; i++) {
      var cfg = readConfig(wrappers[i]);
      if (!cfg.id) continue;
      configs.set(cfg.id, cfg);
      dialogById.set(cfg.id, wrappers[i]); // persistent registry of the live <dialog>
      bindDialog(wrappers[i], cfg);        // once-per-live-dialog close + submit binding
      if (cfg.triggerId) triggerMap[cfg.triggerId] = cfg.id; // ID/anchor binding for ALL popups
      if (armed.has(cfg.id)) continue;
      if (AUTO_TRIGGERS.indexOf(cfg.trigger) !== -1) pending.push(cfg);
      else armed.add(cfg.id); // 'click' = registered, never auto-armed
    }
    bindClickTriggers();
    bindWarmHovers();   // B: warm-prefetch on hover/focus of click/ID/anchor triggers
    bindLaunchers();    // A3: FAB click-bind + converted gating (idempotent per element)

    // Editor: skip coordinator/eligibility entirely; editor preview drives open/close.
    if (window.Shopify && Shopify.designMode) {
      pending.forEach(function (c) { armed.add(c.id); });
      return;
    }

    // B: a wheel with < 2 segments is skipped here too, so it neither arms nor
    // suppresses a valid lower-priority popup in the one-per-pageview coordinator.
    var ok = pending.filter(function (c) { return eligible(c) && !wheelSegBlocked(c); });
    ok.forEach(function (c, idx) { c._domIdx = idx; });                   // DOM-order tiebreaker
    ok.sort(function (a, b) { return (b.priority - a.priority) || (a._domIdx - b._domIdx); });
    pending.forEach(function (c) { armed.add(c.id); });                   // all registered (configs kept)
    if (ok.length) armTrigger(ok[0]);                                     // arm ONLY the highest-priority eligible

    if (!reopenChecked) {
      reopenChecked = true;
      maybeReopen();
    }
  }

  // Programmatic open (later units + manual testing): document.dispatchEvent(
  //   new CustomEvent('popup:open', { detail: { id: '<section.id>' } }))
  document.addEventListener('popup:open', function (e) {
    if (e.detail && e.detail.id) open(e.detail.id);
  });

  // A2: converted hook for A3 (form submit) / B (spin). A2 never dispatches it.
  document.addEventListener('popup:converted', function (e) {
    if (e.detail && e.detail.id) {
      var cfg = configs.get(e.detail.id);
      if (cfg) { convertedOpen.add(e.detail.id); setConverted(cfg); }
      hideLauncherFor(e.detail.id); // A3/B: a converted popup hides its launcher
    }
  });

  // A deferring consumer (the wheel) fires this when its spin has finished. The engine
  // then tags + stashes + fires the native submit. Generic: any future deferring popup
  // dispatches popup:spincomplete with detail.id to trigger the same finalize.
  document.addEventListener('popup:spincomplete', function (e) {
    if (!e.detail || !e.detail.id) return;
    var id = e.detail.id;
    var cfg = configs.get(id);
    var dialog = dialogById.get(id) || document.querySelector('dialog.popup[data-popup-id="' + id + '"]');
    var form = dialog ? dialog.querySelector('form') : (e.detail.form || null);
    if (!cfg || !form) return;
    finalizeSubmit(id, cfg, form);
  });

  // A2: editor preview (designMode only) — open on select, close on deselect.
  document.addEventListener('shopify:section:select', function (e) {
    if (!(window.Shopify && Shopify.designMode)) return;
    if (e.detail && configs.has(e.detail.sectionId)) open(e.detail.sectionId);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (!(window.Shopify && Shopify.designMode)) return;
    if (!e.detail) return;
    var dlg = dialogById.get(e.detail.sectionId);
    if (dlg) close(dlg);
  });

  // Editor re-injection re-registers + re-arms the newly injected section subtree.
  document.addEventListener('shopify:section:load', discover);

  // Engine is defer-loaded so the DOM is parsed; run now (guard just in case).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', discover);
  } else {
    discover();
  }
})();
